import { createFileRoute } from "@tanstack/react-router";
import { ServicePage } from "@/components/ServicePage";
import { RelatedServicesLinks } from "@/components/RelatedServicesLinks";
import {
  Link2,
  Newspaper,
  Building2,
  GraduationCap,
  Award,
  Search,
  Wrench,
  Truck,
  Home as HomeIcon,
  Hammer,
  Briefcase,
  Heart,
} from "lucide-react";

export const Route = createFileRoute("/link-building-colorado")({
  head: () => ({
    meta: [
      { title: "Link Building Colorado | Local Citations & CO Backlinks" },
      {
        name: "description",
        content:
          "Colorado link building done right — local citations, chamber listings, .edu and .gov mentions, Colorado news outlets, and real editorial backlinks. No PBNs, no spam.",
      },
      { property: "og:title", content: "Link Building Colorado | Real Local Backlinks That Move Rankings" },
    ],
    links: [
      { rel: "canonical", href: "https://coloradowebimpressions.com/link-building-colorado" },
    ],
  }),
  component: () => (
    <ServicePage
      slug="link-building"
      serviceName="Link Building"
      pricingRoute="/pricing"
      eyebrow="Colorado Link Building"
      title="Colorado Link Building Built Around Real Local Authority"
      goldWord="Real Local Authority"
      description="Local citations, chamber listings, Colorado .edu and .gov mentions, and real editorial backlinks from Colorado news outlets and industry publications. No PBNs, no link farms, no spam."
      intro={{
        heading: "National link building doesn't translate to Colorado local rankings.",
        body: [
          "The big-agency link-building playbook — bulk outreach, mass guest posts, link inserts on generic blogs — doesn't move the needle for Colorado local businesses. What does: real Colorado authority signals. Local chamber memberships. Citations on the directories Google trusts. Mentions in the Denver Post, Colorado Springs Gazette, BizWest, and 5280 Magazine. Sponsorships with local nonprofits and youth sports. Industry-association links from CEAA, RMACA, BBB Mountain States.",
          "We build link profiles that look exactly like a real, established Colorado business — because that's what Google rewards in local search. Slow, manual, defensible, and tied to actual ranking lift.",
        ],
      }}
      benefits={[
        "Local citation building & cleanup",
        "Colorado chamber & association links",
        "Local press & editorial outreach",
        "Sponsorship & nonprofit links",
        ".edu & .gov mentions where natural",
        "Anchor-text diversity & safety",
      ]}
      features={[
        { icon: Building2, title: "Citation Foundation", desc: "Build and clean up your business listings across Yelp, BBB, Angi, MapQuest, Apple Maps, Bing Places, and 50+ Colorado-relevant directories." },
        { icon: Award, title: "Chamber & Association", desc: "Memberships and links from Colorado chambers (Springs, Denver Metro, BBB Mountain States) and trade associations (CEAA, RMACA, CAR)." },
        { icon: Newspaper, title: "Local Press & Editorial", desc: "Real outreach to Denver Post, Colorado Springs Gazette, BizWest, 5280 Magazine, Westword, and Colorado business publications." },
        { icon: GraduationCap, title: ".edu & .gov", desc: "Natural .edu links via scholarship outreach, alumni programs, and CSU/CU/UCCS partnerships when genuinely relevant." },
        { icon: Link2, title: "Editorial Outreach", desc: "Manual, one-at-a-time outreach for guest posts and resource-page placements on real Colorado-authority sites." },
        { icon: Search, title: "Anchor Diversity", desc: "Branded, naked, and partial-match anchor mix that looks natural — not optimized into a Google penalty." },
      ]}
      frameworkHeading="The Colorado Link Building Method"
      frameworkIntro="A 5-step framework for building real Colorado local authority — slowly, manually, defensibly."
      framework={[
        { number: "01", title: "Backlink Audit", desc: "Full audit of your existing backlinks. Identify spam, toxic links, and disavow opportunities. Map gaps vs Colorado competitors." },
        { number: "02", title: "Citation Foundation", desc: "Build and clean up business listings across the Colorado-relevant directories that move local rankings." },
        { number: "03", title: "Local Authority Layer", desc: "Chamber memberships, trade associations, sponsorships, and nonprofit partnerships — real Colorado business signals." },
        { number: "04", title: "Editorial Outreach", desc: "Manual outreach to Colorado publications, industry blogs, and resource pages. One placement at a time, no automation." },
        { number: "05", title: "Track & Maintain", desc: "Monthly link reports, anchor-text monitoring, and ongoing citation maintenance to keep the profile clean and growing." },
      ]}
      industries={[
        { icon: Wrench, name: "HVAC & Plumbing", desc: "Trade association links, manufacturer dealer pages, and Colorado contractor directories." },
        { icon: Truck, name: "Roofing & Exterior", desc: "GAF/Owens Corning certifications, BBB, and Colorado storm-response press coverage." },
        { icon: Hammer, name: "General Contractors", desc: "AGC of Colorado, HBA Metro Denver, and local builder association links." },
        { icon: HomeIcon, name: "Real Estate", desc: "MLS, NAR, Colorado Association of REALTORS, and local Board of REALTORS links." },
        { icon: Briefcase, name: "Professional Services", desc: "Bar association, CPA society, and Colorado professional-services directories." },
        { icon: Heart, name: "Healthcare", desc: "State licensing boards, hospital affiliations, and Colorado health-association links." },
      ]}
      faqs={[
        { q: "How is local link building different from national link building?", a: "Local link building targets the citations, chambers, associations, sponsorships, and Colorado publications that move local rankings — not generic guest posts on national blogs. The signal is geographic relevance, not raw domain authority." },
        { q: "How many links per month do you build?", a: "Quality over quantity — typically 4–10 manually-built local links per month depending on package. Anything claiming 50+ links/month is either spam, PBN, or low-value." },
        { q: "Do you do PBNs or buy links?", a: "No. Both are explicit Google penalties waiting to happen. Every link we build is either a real citation, a real association, or earned through real editorial outreach." },
        { q: "How long until link building moves rankings?", a: "Local citations move Map Pack rankings in 60–90 days. Editorial backlinks compound over 4–8 months. Link building is the slowest SEO lever, but the most durable when done right." },
        { q: "Do you only build Colorado links?", a: "Mostly yes. Our outreach focuses on Colorado-relevant publications, associations, and directories. National links happen naturally when content earns them — we don't manufacture them." },
      ]}
      sectionsAfterPricing={<RelatedServicesLinks current="seo-services" />}
    />
  ),
});
